import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MessageSquare } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import axios from 'axios';
import AppLayout from '@/components/layout/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['hsl(142, 76%, 36%)', 'hsl(25, 95%, 53%)', 'hsl(197, 71%, 73%)', 'hsl(45, 93%, 47%)', 'hsl(280, 65%, 60%)', 'hsl(340, 75%, 55%)', 'hsl(200, 70%, 50%)'];

export default function PlanPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        const profileRes = await axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true });
        if (!profileRes.data) {
          navigate('/onboarding');
          return;
        }
        setProfile(profileRes.data);

        const recRes = await axios.get(`${BACKEND_URL}/api/recommendations`, { withCredentials: true });
        if (recRes.data && recRes.data.length > 0) {
          setRecommendation(recRes.data[0]);
        }
      } catch (error) {
        console.error('Error:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    navigate('/');
  };

  // Get phases from recommendation (support both old and new format)
  const phases = useMemo(() => {
    if (!recommendation) return [];
    
    // New format: phases array
    if (recommendation.phases && Array.isArray(recommendation.phases)) {
      return recommendation.phases;
    }
    
    // Old format: allocation_plan with phase strings - convert to new format
    if (recommendation.allocation_plan) {
      const phaseMap = {};
      recommendation.allocation_plan.forEach(item => {
        const phaseKey = item.phase || 'all';
        if (!phaseMap[phaseKey]) {
          phaseMap[phaseKey] = {
            phase_number: Object.keys(phaseMap).length + 1,
            name: phaseKey,
            allocations: []
          };
          // Parse month range from phase string
          const match = phaseKey.match(/Month\s*(\d+)(?:\s*-\s*(\d+|\+))?/i);
          if (match) {
            phaseMap[phaseKey].month_start = parseInt(match[1]);
            phaseMap[phaseKey].month_end = match[2] === '+' ? null : (match[2] ? parseInt(match[2]) : parseInt(match[1]));
          }
        }
        phaseMap[phaseKey].allocations.push({
          product: item.product,
          ticker: item.ticker,
          percentage: item.percentage,
          monthly_amount: item.monthly_amount,
          rationale: item.rationale
        });
      });
      return Object.values(phaseMap).sort((a, b) => (a.month_start || 0) - (b.month_start || 0));
    }
    
    return [];
  }, [recommendation]);

  const monthlyBudget = recommendation?.monthly_budget || 
    phases[0]?.allocations?.reduce((sum, a) => sum + (a.monthly_amount || 0), 0) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!recommendation || phases.length === 0) {
    return (
      <AppLayout user={user} onLogout={handleLogout}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="mb-4">No investment plan found</p>
            <Button onClick={() => navigate('/onboarding')}>Complete Onboarding</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const parseNetworth = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const cleaned = String(value).replace(/[$,]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const currentNetworth = parseNetworth(profile?.net_assets);
  
  // Get current phase and its allocations
  const currentPhase = phases[selectedPhaseIndex] || phases[0];
  const currentAllocations = (currentPhase?.allocations || []).map(item => ({
    product: item.product,
    name: item.ticker || item.product?.split(' ')[0] || 'Unknown',
    ticker: item.ticker,
    value: item.percentage || 0,
    monthly_amount: item.monthly_amount || 0,
    rationale: item.rationale || ''
  }));
  
  const monthlyTotal = currentAllocations.reduce((sum, item) => sum + item.monthly_amount, 0);

  // Format phase label for tabs
  const formatPhaseLabel = (phase) => {
    const start = phase.month_start || 1;
    const end = phase.month_end;
    if (end === null || end === undefined) {
      return `M${start}+`;
    }
    return `M${start}-${end}`;
  };

  // Calculate growth projections
  const avgGrowthRate = 0.075; // Default 7.5% if not specified
  const calculateFutureValue = (years) => {
    if (years === 0) return currentNetworth;
    const currentGrowth = currentNetworth * Math.pow(1 + avgGrowthRate, years);
    const monthlyRate = avgGrowthRate / 12;
    const months = years * 12;
    const contributionsGrowth = monthlyBudget * (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate * (1 + monthlyRate);
    return Math.round(currentGrowth + contributionsGrowth);
  };

  const annualContribution = monthlyBudget * 12;
  const growthChartData = [];
  for (let year = 0; year <= 10; year++) {
    growthChartData.push({
      year: year,
      invested: currentNetworth + (annualContribution * year),
      projected: calculateFutureValue(year)
    });
  }

  const fiveYearTarget = calculateFutureValue(5);
  const tenYearTarget = calculateFutureValue(10);

  return (
    <AppLayout user={user} onLogout={handleLogout}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">My Investment Plan</h1>
            <p className="text-muted-foreground">Your personalized investment strategy</p>
          </div>
          <Button onClick={() => navigate('/chat')}>
            <MessageSquare className="w-4 h-4 mr-2" />
            Modify with AI
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Current Net Worth</p>
            <p className="text-2xl font-bold font-mono">${currentNetworth.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Monthly Budget</p>
            <p className="text-2xl font-bold font-mono">${monthlyBudget.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">5-Year Target</p>
            <p className="text-2xl font-bold font-mono">${fiveYearTarget.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">10-Year Target</p>
            <p className="text-2xl font-bold font-mono">${tenYearTarget.toLocaleString()}</p>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">Allocation by Phase</h3>
              <p className="text-sm text-muted-foreground mb-4">Select a phase to view its allocation</p>
              <div className="flex flex-wrap gap-2">
                {phases.map((phase, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPhaseIndex(idx)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedPhaseIndex === idx
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80 text-foreground'
                    }`}
                  >
                    {phase.name || `Phase ${phase.phase_number}`}
                    <span className={`ml-1 ${selectedPhaseIndex === idx ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      ({formatPhaseLabel(phase)})
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={currentAllocations}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name} ${value}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {currentAllocations.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}% ($${props.payload.monthly_amount.toLocaleString()})`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">{currentPhase?.name || 'Phase'} Strategy:</p>
              {currentAllocations.map((item, idx) => (
                <p key={idx} className="text-xs text-muted-foreground mb-1">
                  • {item.name}: {item.rationale}
                </p>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">Expected Growth (10 Years)</h3>
              <p className="text-sm text-muted-foreground">Based on ~7.5% average annual return</p>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growthChartData}>
                  <defs>
                    <linearGradient id="investedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(197, 71%, 73%)" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="hsl(197, 71%, 73%)" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Years', position: 'insideBottom', offset: -5 }}
                    ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    domain={[currentNetworth * 0.9, 'auto']}
                  />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="invested" 
                    stroke="hsl(197, 71%, 73%)" 
                    strokeWidth={3} 
                    fill="url(#investedGradient)"
                    name="Total Invested"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="projected" 
                    stroke="hsl(142, 76%, 36%)" 
                    strokeWidth={4} 
                    fill="url(#projectedGradient)"
                    name="Projected Value"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Phase Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{currentPhase?.name || 'Investment Plan'}</h3>
              <span className="text-xl font-bold font-mono text-primary">${monthlyTotal.toLocaleString()}/mo</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {currentPhase?.month_end 
                ? `Month ${currentPhase.month_start} - ${currentPhase.month_end}`
                : `Month ${currentPhase?.month_start || 1}+`
              }
            </p>
            <div className="space-y-4">
              {currentAllocations.map((item, idx) => (
                <div key={idx} className="pb-4 border-b last:border-0">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">{item.product}</span>
                    <span className="font-mono font-semibold">${item.monthly_amount.toLocaleString()}</span>
                  </div>
                  {item.ticker && <p className="text-xs font-mono text-primary mb-1">Ticker: {item.ticker}</p>}
                  <p className="text-sm text-muted-foreground">{item.rationale}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4">Overall Strategy</h3>
            <p className="text-muted-foreground leading-relaxed mb-6">{recommendation.reasoning}</p>
            
            {recommendation.calculations && (
              <>
                <h4 className="text-lg font-semibold mb-3">Phase Calculations</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {Object.entries(recommendation.calculations).map(([key, value]) => (
                    <p key={key}>
                      <strong>{key.replace(/_/g, ' ')}:</strong>{' '}
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </p>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>

        {/* All Phases Overview */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-4">All Phases Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {phases.map((phase, idx) => (
              <div 
                key={idx} 
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedPhaseIndex === idx ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedPhaseIndex(idx)}
              >
                <h4 className="font-semibold mb-1">{phase.name || `Phase ${phase.phase_number}`}</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  {phase.month_end 
                    ? `Month ${phase.month_start} - ${phase.month_end}`
                    : `Month ${phase.month_start}+`
                  }
                </p>
                <div className="space-y-1">
                  {phase.allocations?.slice(0, 3).map((alloc, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span>{alloc.ticker || alloc.product?.split(' ')[0]}</span>
                      <span className="font-mono">{alloc.percentage}%</span>
                    </div>
                  ))}
                  {(phase.allocations?.length || 0) > 3 && (
                    <p className="text-xs text-muted-foreground">+{phase.allocations.length - 3} more...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
